/**
 * useTransitionMatrix Hook
 * 
 * A custom hook that manages the transition matrix state and validation for card movements
 * between different columns. It handles fetching the matrix from Firestore, caching it in memory,
 * and providing functions to check if transitions are allowed.
 * 
 * Admin-only access is enforced for matrix modifications, while all users can view transitions.
 */

import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../Components/auth/firebaseConfig';

export const useTransitionMatrix = (projectId) => {
  // State for storing the transition matrix configuration
  const [transitionMatrix, setTransitionMatrix] = useState(null);
  // Loading state for async operations
  const [loading, setLoading] = useState(true);
  // Error state for handling failures
  const [error, setError] = useState(null);

  // Effect to fetch the transition matrix from Firestore when projectId changes
  useEffect(() => {
    const fetchTransitionMatrix = async () => {
      // Skip if no projectId is provided
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        // Attempt to fetch the matrix from Firestore using the project's settings collection
        const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
        const matrixDoc = await getDoc(matrixRef);
        
        if (matrixDoc.exists()) {
          // Matrix exists in Firestore, load it into state
          const data = matrixDoc.data();
          console.log('Raw transition matrix:', data);
          setTransitionMatrix(data);
        } else {
          // No matrix found, default to allowing all transitions by setting matrix to null
          console.log('No transition matrix found, allowing all transitions');
          setTransitionMatrix(null);
        }
        setError(null);
      } catch (err) {
        // Handle any errors during fetch by clearing matrix and setting error state
        console.error('Error fetching transition matrix:', err);
        setError(err.message);
        setTransitionMatrix(null);
      } finally {
        // Always mark loading as complete
        setLoading(false);
      }
    };

    fetchTransitionMatrix();
  }, [projectId]);

  /**
   * Checks if a transition between two columns is allowed based on the matrix
   * @param {string} fromColumn - The source column
   * @param {string} toColumn - The target column
   * @returns {boolean} - Whether the transition is allowed
   */
  const isTransitionAllowed = (fromColumn, toColumn) => {
    // Debug logging for transition check
    console.log('Checking transition:', {
      from: fromColumn,
      to: toColumn,
      matrix: transitionMatrix
    });

    // If no matrix is defined or there's an error, allow all transitions
    // This provides a fallback when the matrix hasn't been configured
    if (!transitionMatrix || error) {
      console.log('No matrix or error, allowing transition');
      return true;
    }

    // Direct lookup in the matrix without normalization
    // The matrix structure is: { fromColumn: { toColumn: true/false } }
    const isAllowed = Boolean(transitionMatrix[fromColumn]?.[toColumn]);
    console.log('Transition allowed:', isAllowed);
    return isAllowed;
  };

  /**
   * Updates the transition matrix. This function is only available to admin users.
   * The matrix defines which card movements are allowed between columns.
   * 
   * @param {Object} newMatrix - The new transition matrix configuration
   *                            Format: { fromColumn: { toColumn: boolean } }
   * @param {string} userRole - The role of the current user
   * @throws {Error} If the current user is not an admin or role is undefined
   */
  const updateTransitionMatrix = async (newMatrix, userRole) => {
    try {
      // Log the role for debugging purposes
      console.log('Updating matrix, user role:', userRole);

      // Validate that we have a role value
      if (!userRole) {
        throw new Error('User role is undefined');
      }

      // Ensure only admin users can modify the matrix
      // Case-insensitive check to handle variations like 'Admin', 'admin', 'ADMIN'
      if (userRole.toLowerCase() !== 'admin') {
        throw new Error('Only administrators can modify transition settings');
      }

      // If we have a valid projectId, save the matrix to Firestore
      if (projectId) {
        const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
        // Use setDoc to create or update the matrix document
        await setDoc(matrixRef, newMatrix);
      }

      // Update the local state with the new matrix
      // This ensures immediate UI updates without needing to refetch
      setTransitionMatrix(newMatrix);
    } catch (error) {
      // Log and rethrow any errors for proper error handling in the UI
      console.error('Error updating transition matrix:', error);
      throw error;
    }
  };

  /**
   * Resets the transition matrix to default (allows all transitions).
   * This function is only available to admin users.
   * 
   * @param {string} userRole - The role of the current user
   * @throws {Error} If the current user is not an admin or role is undefined
   */
  const resetTransitionMatrix = async (userRole) => {
    try {
      // Log the role for debugging purposes
      console.log('Resetting matrix, user role:', userRole);

      // Validate that we have a role value
      if (!userRole) {
        throw new Error('User role is undefined');
      }

      // Ensure only admin users can reset the matrix
      // Case-insensitive check to handle variations like 'Admin', 'admin', 'ADMIN'
      if (userRole.toLowerCase() !== 'admin') {
        throw new Error('Only administrators can reset transition settings');
      }

      // If we have a valid projectId, delete the matrix document from Firestore
      if (projectId) {
        const matrixRef = doc(db, `projectSettings/${projectId}/stateTransitions/matrix`);
        await deleteDoc(matrixRef);
      }

      // Set local state to null (allows all transitions)
      setTransitionMatrix(null);
    } catch (error) {
      // Log and rethrow any errors for proper error handling in the UI
      console.error('Error resetting transition matrix:', error);
      throw error;
    }
  };

  // Return the hook's interface with all necessary functions and state
  return {
    transitionMatrix,      // The current transition matrix configuration
    setTransitionMatrix: updateTransitionMatrix,   // Admin-only function to update the matrix
    resetTransitionMatrix, // Admin-only function to reset the matrix to default
    loading,              // Whether the matrix is currently loading
    error,                // Any error that occurred during matrix operations
    isTransitionAllowed   // Function to check if a specific transition is allowed
  };
} 